import * as yup from "yup";

// No character rule on either field, deliberately. `CreateStatusRequest` takes
// any non-blank name up to 80 characters and any non-blank description up to
// 500 (candidate_status/schema/api.py), and `_code_from_name` derives
// "ROUND_2" from "Round 2" without complaint. The letters-only rule that used
// to live here was borrowed from the tenant-user name field, where a person's
// name really is letters — a pipeline stage is not a person, and it left the
// numbered stages most tenants run ("Round 2", "L2", "Phase 3") unnameable
// through the only screen that creates them. `.claude/rules/registries.md`:
// the form refuses what the server refuses, with the same limit.

export const candidateStatusValidationSchema = yup
  .object({
    name: yup
      .string()
      .transform((v) => (typeof v === "string" ? v.trim() : v))
      .required("Name is required")
      .min(1, "Name is required")
      .max(80, "Name cannot exceed 80 characters"),

    description: yup
      .string()
      .transform((v) => (typeof v === "string" ? v.trim() : v))
      .required("Description is required")
      .min(1, "Description is required")
      .max(500, "Description cannot exceed 500 characters"),

    insertBeforeId: yup.string().nullable().default(null),

    allowedTransitionIds: yup
      .array()
      .of(yup.string().uuid("Each transition must be a valid UUID"))
      .default([]),

    sequence: yup
      .number()
      .nullable()
      .transform((v) => (v === "" || v === null || v === undefined ? null : Number(v)))
      .min(1, "Step must be at least 1")
      .test(
        "required-for-create",
        "Please choose where this status should be placed",
        function (value) {
          const mode = this?.options?.context?.mode;
          if (mode === "add" && (value === null || value === undefined)) return false;
          return true;
        }
      ),

    isInitial: yup.boolean().default(false),
    isHired: yup.boolean().default(false),
    isRejected: yup.boolean().default(false),
    isReusable: yup.boolean().default(false),
    isInterviewEligible: yup.boolean().default(false),
  })
  .test(
    "single-status-role",
    "A status can be only one of Initial, Hired, or Rejected",
    (value) => {
      const count = [
        value?.isInitial,
        value?.isHired,
        value?.isRejected,
      ].filter(Boolean).length;

      return count <= 1;
    }
  )
  .test(
    "reusable-not-combinable",
    "A reusable status cannot also be Initial, Hired, or Rejected",
    (value) => {
      if (!value?.isReusable) return true;
      return !(value?.isInitial || value?.isHired || value?.isRejected);
    }
  )
  .test(
    "interview-eligible-not-terminal",
    "A Hired or Rejected status cannot also allow interview scheduling",
    (value) => {
      if (!value?.isInterviewEligible) return true;
      return !(value?.isHired || value?.isRejected);
    }
  )
  .test(
    "terminal-no-transitions",
    "Hired or Rejected status cannot have outgoing transitions",
    (value) => {
      if (!value?.isRejected && !value?.isHired) return true;
      return !value?.allowedTransitionIds?.length;
    }
  )
  .test(
    "sequence-1-must-be-initial",
    "Step 1 is reserved for the INITIAL status",
    function (value) {
      const mode = this?.options?.context?.mode;
      if (mode === "edit") return true;
      if (value?.sequence === 1 && !value?.isInitial) return false;
      return true;
    }
  )
  .test(
    "initial-must-be-sequence-1",
    "The INITIAL status must be at step 1",
    function (value) {
      const mode = this?.options?.context?.mode;
      if (mode === "edit") return true;
      if (value?.isInitial && value?.sequence !== 1) return false;
      return true;
    }
  );

export const candidateStatusFlowValidationSchema = yup.object({
  name: yup
    .string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .required("Name is required")
    .min(1, "Name is required")
    .max(80, "Name cannot exceed 80 characters"),

  description: yup
    .string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .max(500, "Description cannot exceed 500 characters")
    .nullable(),

  isActive: yup.boolean().default(true),
});